import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, Building2, Utensils, Stethoscope, Code, Briefcase, MoreHorizontal } from "lucide-react";
import { RitualIcon } from "@/components/ui/RitualIcon";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface OutletContext {
  user: any;
  organizationId: string;
}

const industries = [
  { id: "agency", label: "Agency / Studio", icon: Briefcase },
  { id: "food", label: "Food & Hospitality", icon: Utensils },
  { id: "healthcare", label: "Healthcare", icon: Stethoscope },
  { id: "tech", label: "Tech / SaaS", icon: Code },
  { id: "retail", label: "Retail / E-com", icon: Building2 },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

const painPoints = [
  "New hire onboarding takes too long",
  "Daily checklists get missed",
  "No visibility into task completion",
  "Quality/compliance issues",
  "Knowledge stuck in people's heads",
  "Projects slip through cracks",
];

const Setup = () => {
  const { organizationId } = useOutletContext<OutletContext>();
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState({ roles: 0, sops: 0, runs: 0 });
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    industry: "",
    pains: [] as string[],
    topWorkflows: "",
  });

  const togglePain = (pain: string) => {
    if (formData.pains.includes(pain)) {
      setFormData({ ...formData, pains: formData.pains.filter((p) => p !== pain) });
    } else {
      setFormData({ ...formData, pains: [...formData.pains, pain] });
    }
  };

  const handleGenerate = async () => {
    if (!formData.industry) {
      toast({
        variant: "destructive",
        title: "Select an industry",
        description: "Please select your industry to continue.",
      });
      return;
    }

    setLoading(true);
    try {
      const { error: profileError } = await supabase
        .from("onboarding_profiles")
        .insert({
          organization_id: organizationId,
          answers_json: formData,
          processed_by_ai: false,
        });

      if (profileError) throw profileError;

      const { data, error } = await supabase.functions.invoke("ops-copilot", {
        body: {
          answers: {
            orgType: formData.industry,
            breakpoints: formData.pains,
            repeatedWorkflows: formData.topWorkflows,
          },
          organizationId,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setSummary(data.summary);
      setCompleted(true);
      
      toast({
        title: "Your Ritual is ready!",
        description: "We've generated your operations playbook.",
      });
    } catch (error: any) {
      console.error("Setup error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to generate. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (completed) {
    return (
      <div className="container max-w-lg py-6 sm:py-12 px-4 pb-24">
        <Card className="border-2">
          <CardHeader className="text-center p-4 sm:p-6">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            </div>
            <CardTitle className="text-xl sm:text-2xl">You're all set!</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Your operations platform is ready
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold text-primary">{summary.roles}</div>
                <div className="text-xs text-muted-foreground">Roles</div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold text-primary">{summary.sops}</div>
                <div className="text-xs text-muted-foreground">SOPs</div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-2xl sm:text-3xl font-bold text-primary">{summary.runs}</div>
                <div className="text-xs text-muted-foreground">Runs</div>
              </div>
            </div>

            <div className="space-y-2 pt-4">
              <Button
                className="w-full h-12"
                variant="hero"
                onClick={() => navigate("/app/tasks")}
              >
                View My Tasks
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate("/app/sops")}
              >
                Explore SOPs
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-lg py-4 sm:py-8 px-4 pb-24">
      <div className="flex items-center gap-2 mb-6">
        <RitualIcon size="md" />
        <span className="font-semibold">AI Setup</span>
      </div>

      <div className="space-y-6">
        {/* Industry Selection */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">What's your industry?</h2>
          <div className="grid grid-cols-2 gap-2">
            {industries.map((ind) => (
              <button
                key={ind.id}
                onClick={() => setFormData({ ...formData, industry: ind.id })}
                className={`p-3 sm:p-4 rounded-lg border-2 text-left flex items-center gap-3 transition-all ${
                  formData.industry === ind.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <ind.icon className={`h-5 w-5 ${formData.industry === ind.id ? "text-primary" : "text-muted-foreground"}`} />
                <span className="text-sm font-medium">{ind.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Pain Points */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">What's breaking?</h2>
          <p className="text-sm text-muted-foreground">Select all that apply</p>
          <div className="space-y-2">
            {painPoints.map((pain) => (
              <div
                key={pain}
                onClick={() => togglePain(pain)}
                className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${
                  formData.pains.includes(pain)
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <Checkbox
                  checked={formData.pains.includes(pain)}
                  className="pointer-events-none"
                />
                <Label className="cursor-pointer text-sm flex-1">{pain}</Label>
              </div>
            ))}
          </div>
        </div>

        {/* Top Workflows */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Top workflows (optional)</h2>
          <Textarea
            placeholder="e.g., Client onboarding, Daily kitchen prep, Monthly reporting..."
            value={formData.topWorkflows}
            onChange={(e) => setFormData({ ...formData, topWorkflows: e.target.value })}
            rows={3}
            className="resize-none"
          />
        </div>

        {/* Generate Button */}
        <Button 
          onClick={handleGenerate} 
          disabled={loading || !formData.industry} 
          variant="hero" 
          className="w-full h-12 text-base"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating your Ritual...
            </>
          ) : (
            <>
              <RitualIcon size="sm" className="mr-2" />
              Generate My SOPs
            </>
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          AI will create role-based playbooks tailored to your needs
        </p>
      </div>
    </div>
  );
};

export default Setup;
